import { useState } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { lumi } from '@/lib/lumi'
import { track } from '@/lib/analytics'

export default function Footer() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error('Please complete all fields')
      return
    }
    setLoading(true)
    try {
      await lumi.entities.contact_messages.create({
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
        subject: 'Website contact form',
        status: 'new',
        createdAt: new Date().toISOString(),
      })
      await track('contact_submit', '/contact')
      toast.success('Message received — thank you')
      setName('')
      setEmail('')
      setMessage('')
    } catch (err) {
      console.error(err)
      toast.error('Could not send. Try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <footer className="border-t-2 border-[#0A0A0A] bg-[#0A0A0A] text-[#FAFAF7] mt-32">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="eyebrow text-[#E63946] mb-4">/ 04 Contact</p>
            <h2 className="headline text-3xl lg:text-5xl mb-6">
              Got a question?<br/>
              <span className="text-[#E63946]">Ask us.</span>
            </h2>
            <p className="text-[#FAFAF7]/60 max-w-md text-[15px] leading-relaxed">
              Educators, students, parents — drop us a note. We answer every message about calculator skills, basic math computation, and pedagogy within two business days.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              aria-label="Your name"
              type="text"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-transparent border-2 border-[#FAFAF7]/20 px-4 py-3 text-sm placeholder-[#FAFAF7]/40 focus:border-[#E63946] focus:outline-none transition-colors"
            />
            <input
              aria-label="Your email"
              type="email"
              placeholder="you@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-transparent border-2 border-[#FAFAF7]/20 px-4 py-3 text-sm placeholder-[#FAFAF7]/40 focus:border-[#E63946] focus:outline-none transition-colors"
            />
            <textarea
              aria-label="Your message"
              placeholder="What would you like to ask?"
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="sm:col-span-2 bg-transparent border-2 border-[#FAFAF7]/20 px-4 py-3 text-sm placeholder-[#FAFAF7]/40 focus:border-[#E63946] focus:outline-none transition-colors resize-none"
            />
            <button
              type="submit"
              disabled={loading}
              className="sm:col-span-2 bg-[#E63946] text-white py-3.5 font-semibold tracking-tight hover:bg-[#FAFAF7] hover:text-[#0A0A0A] transition-colors disabled:opacity-50"
            >
              {loading ? 'Sending…' : 'Send message →'}
            </button>
          </form>
        </div>

        <div className="mt-20 pt-8 border-t border-[#FAFAF7]/10 grid grid-cols-1 md:grid-cols-3 gap-6 text-xs text-[#FAFAF7]/50">
          <div>
            <p className="font-extrabold text-[#FAFAF7] tracking-tight text-base mb-2">CalculatorSkills.com</p>
            <p>Improving foundational computation skills in math — one keystroke at a time.</p>
          </div>
          <div>
            <p className="eyebrow text-[#FAFAF7] mb-3">Pages</p>
            <ul className="space-y-1.5">
              <li><Link to="/" className="hover:text-[#FAFAF7]">Home</Link></li>
              <li><Link to="/calculator" className="hover:text-[#FAFAF7]">Praxis Core Calculator</Link></li>
              <li><Link to="/blog" className="hover:text-[#FAFAF7]">Math Computation Blog</Link></li>
              <li><Link to="/about" className="hover:text-[#FAFAF7]">About Us</Link></li>
            </ul>
          </div>
          <div>
            <p className="eyebrow text-[#FAFAF7] mb-3">Legal</p>
            <p className="leading-relaxed">
              Privacy: We collect anonymous page-view analytics to improve content. We never sell or share your data. Contact submissions are stored securely and used only to reply to you.
            </p>
            <p className="mt-3">© {new Date().getFullYear()} CalculatorSkills.com</p>
          </div>
        </div>
      </div>
    </footer>
  )
}
